<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[130] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm md:items-center md:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-pdf-studio-title"
      @click.self="closeStudio"
    >
      <section class="flex h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-t-[1.75rem] bg-[#f4f1ea] shadow-2xl md:h-[92vh] md:rounded-[1.75rem]">
        <header class="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 md:px-6 md:py-4">
          <div class="flex min-w-0 items-center gap-3">
            <span class="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-slate-900 text-amber-300 shadow-lg shadow-slate-900/15">
              <font-awesome-icon icon="fa-solid fa-file-pdf" class="h-4 w-4" />
            </span>
            <div class="min-w-0">
              <div class="mb-0 flex items-center gap-2">
                <h2 id="tour-pdf-studio-title" class="mb-0 truncate text-base font-black text-slate-950 md:text-xl">PDF Stüdyosu</h2>
                <span class="hidden rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-[.14em] text-amber-800 sm:inline">İnanç Select</span>
              </div>
              <p class="mb-0 text-[11px] text-slate-500 md:text-xs">{{ tours.length }} program · profesyonel A4 teklif dosyası</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="hidden items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 sm:inline-flex"
              @click="emit('clear')"
            >
              <font-awesome-icon icon="fa-solid fa-trash" class="h-3 w-3" />
              Seçimi temizle
            </button>
            <button
              type="button"
              class="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-950"
              aria-label="PDF stüdyosunu kapat"
              @click="closeStudio"
            >
              <font-awesome-icon icon="fa-solid fa-xmark" class="h-4 w-4" />
            </button>
          </div>
        </header>

        <div class="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1.02fr)_minmax(420px,.98fr)]">
          <div class="min-h-0 overflow-y-auto border-r border-slate-200 bg-white p-4 md:p-6">
            <div class="space-y-6">
              <section>
                <div class="mb-3 flex items-center justify-between">
                  <div>
                    <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-amber-700">01 · Düzen</p>
                    <h3 class="mb-0 mt-1 text-sm font-black text-slate-900">Sunum formatını seçin</h3>
                  </div>
                  <span class="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-500">A4 · Dikey</span>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <button
                    v-for="option in formatOptions"
                    :key="option.value"
                    type="button"
                    class="group rounded-2xl border p-3 text-left transition"
                    :class="settings.format === option.value ? 'border-amber-500 bg-amber-50 shadow-sm ring-1 ring-amber-400' : 'border-slate-200 bg-white hover:border-slate-300'"
                    @click="settings.format = option.value"
                  >
                    <div class="mb-3 flex h-16 items-center justify-center rounded-xl bg-slate-900 p-2">
                      <div v-if="option.value === 'compact'" class="w-full space-y-1">
                        <span v-for="line in 3" :key="line" class="block h-2 rounded-sm bg-white/90" :style="{ width: `${100 - line * 8}%` }"></span>
                      </div>
                      <div v-else class="grid w-full grid-cols-2 gap-1.5">
                        <span class="h-11 rounded bg-gradient-to-br from-amber-200 to-amber-500"></span>
                        <span class="space-y-1 pt-1"><i class="block h-2 rounded bg-white/90"></i><i class="block h-2 w-4/5 rounded bg-white/60"></i><i class="block h-2 w-2/3 rounded bg-white/40"></i></span>
                      </div>
                    </div>
                    <strong class="block text-xs text-slate-900">{{ option.label }}</strong>
                    <span class="mt-1 block text-[10px] leading-4 text-slate-500">{{ option.description }}</span>
                  </button>
                </div>
              </section>

              <section v-if="isAdmin" class="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-amber-700">Danışman Notu</p>
                <h3 class="mb-1 mt-1 text-sm font-black text-slate-900">Kapak mesajını özelleştirin</h3>
                <p class="mb-3 text-[10px] leading-4 text-slate-500">Boş bırakırsanız standart bilgilendirme mesajı kullanılır.</p>
                <textarea v-model.trim="settings.message" rows="3" maxlength="360" class="w-full resize-none rounded-xl border-amber-200 bg-white px-3 py-2.5 text-sm leading-5 focus:border-amber-500 focus:ring-amber-500" placeholder="Müşteriye özel kapak mesajı..."></textarea>
                <span class="mt-1 block text-right text-[9px] text-slate-400">{{ settings.message.length }}/360</span>
              </section>

              <section>
                <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-amber-700">02 · Organizasyon</p>
                <h3 class="mb-3 mt-1 text-sm font-black text-slate-900">Turları gruplayın</h3>
                <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <button
                    v-for="option in groupOptions"
                    :key="option.value"
                    type="button"
                    class="rounded-xl border px-2 py-2.5 text-[10px] font-bold transition"
                    :class="settings.groupBy === option.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'"
                    @click="settings.groupBy = option.value"
                  >
                    <font-awesome-icon :icon="option.icon" class="mr-1 h-3 w-3" />
                    {{ option.label }}
                  </button>
                </div>
              </section>

              <section>
                <div class="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-amber-700">03 · Vurgular</p>
                    <h3 class="mb-0 mt-1 text-sm font-black text-slate-900">Öne çıkanları görünür yapın</h3>
                  </div>
                  <span class="text-[10px] text-slate-400">Otomatik algılanır</span>
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <label class="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <input v-model="settings.includeCover" type="checkbox" class="rounded border-slate-300 text-amber-600 focus:ring-amber-500">
                    <span class="grid h-8 w-8 place-items-center rounded-lg bg-amber-100 text-amber-700"><font-awesome-icon icon="fa-solid fa-image" class="h-3 w-3" /></span>
                    <span><strong class="block text-[11px] text-slate-800">Kapak ve marka sayfası</strong><small class="text-[9px] text-slate-500">Rezervasyon PDF'iyle aynı sunum dili</small></span>
                  </label>
                  <label class="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <input v-model="settings.highlightFairmont" type="checkbox" class="rounded border-slate-300 text-amber-600 focus:ring-amber-500">
                    <span class="grid h-8 w-8 place-items-center rounded-lg bg-amber-100 text-amber-700"><font-awesome-icon icon="fa-solid fa-gem" class="h-3 w-3" /></span>
                    <span><strong class="block text-[11px] text-slate-800">Fairmont vurgusu</strong><small class="text-[9px] text-slate-500">{{ stats.fairmont }} eşleşen program</small></span>
                  </label>
                  <label class="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <input v-model="settings.highlightTrain" type="checkbox" class="rounded border-slate-300 text-amber-600 focus:ring-amber-500">
                    <span class="grid h-8 w-8 place-items-center rounded-lg bg-teal-100 text-teal-700"><font-awesome-icon icon="fa-solid fa-train" class="h-3 w-3" /></span>
                    <span><strong class="block text-[11px] text-slate-800">Hızlı tren vurgusu</strong><small class="text-[9px] text-slate-500">{{ stats.train }} eşleşen program</small></span>
                  </label>
                  <label class="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <input v-model="settings.showCampaigns" type="checkbox" class="rounded border-slate-300 text-amber-600 focus:ring-amber-500">
                    <span class="grid h-8 w-8 place-items-center rounded-lg bg-slate-200 text-slate-700"><font-awesome-icon icon="fa-solid fa-tags" class="h-3 w-3" /></span>
                    <span><strong class="block text-[11px] text-slate-800">Kampanya etiketleri</strong><small class="text-[9px] text-slate-500">Varsa özel mesajı gösterir</small></span>
                  </label>
                </div>
              </section>

              <section>
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-amber-700">04 · Seçimler</p>
                    <h3 class="mb-0 mt-1 text-sm font-black text-slate-900">PDF'e girecek turlar</h3>
                  </div>
                  <span class="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-black text-white">{{ tours.length }}</span>
                </div>
                <div class="max-h-52 space-y-2 overflow-auto pr-1">
                  <div v-for="tour in normalizedTours" :key="tour.id" class="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5">
                    <img v-if="tour.mainImage" :src="tour.mainImage" alt="" class="h-11 w-14 shrink-0 rounded-lg object-cover">
                    <span v-else class="grid h-11 w-14 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-400"><font-awesome-icon icon="fa-solid fa-kaaba" /></span>
                    <div class="min-w-0 flex-1">
                      <strong class="block truncate text-[11px] text-slate-900">{{ tour.startLongLabel }} · {{ tour.duration }} gün</strong>
                      <span class="block truncate text-[10px] text-slate-500">{{ tour.title }}</span>
                    </div>
                    <button type="button" class="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" :aria-label="`${tour.title} turunu çıkar`" @click="emit('remove', tour.id)">
                      <font-awesome-icon icon="fa-solid fa-xmark" class="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <aside class="hidden min-h-0 overflow-y-auto bg-[#e8e4da] p-6 lg:block">
            <div class="mb-3 flex items-center justify-between">
              <div>
                <p class="mb-0 text-[10px] font-black uppercase tracking-[.18em] text-slate-500">Canlı Önizleme</p>
                <span class="text-[10px] text-slate-400">İlk sayfanın temsili görünümü</span>
              </div>
              <span class="rounded-full bg-white/70 px-2.5 py-1 text-[9px] font-bold text-slate-500 shadow-sm">210 × 297 mm</span>
            </div>
            <div class="mx-auto aspect-[210/297] w-full max-w-[520px] overflow-hidden bg-white shadow-[0_30px_80px_-28px_rgba(15,23,42,.35)]">
              <div v-if="settings.includeCover" class="relative h-full bg-cover bg-center" :style="{ backgroundImage: `url(${absoluteCoverUrl})` }">
                <div class="absolute inset-x-[10%] top-[25%] text-center">
                  <h3 class="mx-auto max-w-[85%] text-2xl font-extrabold leading-tight tracking-tight text-slate-800">{{ automaticTitle }}</h3>
                  <p class="mx-auto mt-4 max-w-[80%] text-[9px] leading-4 text-slate-500">{{ effectiveMessage }}</p>
                </div>
                <div class="absolute bottom-4 right-6 text-[7px] font-bold text-white">{{ tours.length }} program</div>
              </div>
              <div v-else class="h-full p-[7%]">
                <div class="flex items-start justify-between border-b border-slate-200 pb-4">
                  <img src="/assets/img/logo/inanc_tur_yeni_logo.webp" alt="İnanç Turizm" class="h-9 w-auto object-contain">
                  <div class="max-w-[65%] text-right">
                    <h3 class="mb-1 text-base font-extrabold tracking-tight text-slate-800">{{ automaticTitle }}</h3>
                    <p class="text-[7px] text-slate-400">Tarih, ulaşım, konaklama ve fiyat ayrıntılarıyla seçtiğiniz programlar.</p>
                  </div>
                </div>
                <div class="mt-4 grid grid-cols-3 gap-2">
                  <div v-for="summary in previewSummary" :key="summary.label" class="rounded border border-slate-200 bg-slate-50 p-2">
                    <span class="block text-[5px] font-black uppercase tracking-wider text-slate-400">{{ summary.label }}</span>
                    <strong class="mt-1 block text-[8px] text-slate-800">{{ summary.value }}</strong>
                  </div>
                </div>
                <div class="mt-4 space-y-2">
                  <div v-for="tour in normalizedTours.slice(0, settings.format === 'compact' ? 5 : 3)" :key="`preview-${tour.id}`" class="overflow-hidden rounded border border-slate-200">
                    <template v-if="settings.format === 'compact'">
                      <div class="grid grid-cols-[55px_1fr_58px] items-center gap-2 p-2">
                        <div><strong class="block text-[7px] text-slate-800">{{ tour.startLabel }}</strong><span class="text-[5px] text-slate-400">{{ tour.duration }} gün</span></div>
                        <div><strong class="block truncate text-[7px] text-slate-700">{{ tour.hotels[0]?.name }}</strong><span class="block truncate text-[5px] text-slate-400">{{ tour.hotels[1]?.name }}</span></div>
                        <strong class="text-right text-[8px] text-slate-800">{{ tour.priceLabel }} <small>{{ tour.currency }}</small></strong>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex items-center gap-2 bg-slate-800 p-2.5 text-white">
                        <span class="text-[9px] font-black text-amber-300">{{ tour.startLabel }}</span>
                        <strong class="min-w-0 flex-1 truncate text-[7px]">{{ tour.title }}</strong>
                        <strong class="text-[9px]">{{ tour.priceLabel }} <small>{{ tour.currency }}</small></strong>
                      </div>
                      <div class="grid grid-cols-2 gap-2 p-2">
                        <span v-for="hotel in tour.hotels" :key="hotel.name" class="truncate text-[6px] text-slate-500"><b class="text-slate-700">{{ hotel.city }}</b> · {{ hotel.name }}</span>
                      </div>
                      <div class="grid grid-cols-3 gap-1 border-t border-slate-100 px-2 py-1.5">
                        <span v-for="route in tour.routes.slice(0, 3)" :key="`${tour.id}-${route.label}`" class="truncate text-[5px] text-slate-500"><b class="text-teal-700">{{ route.label }}</b> {{ route.from }} → {{ route.to }}</span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <footer class="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 md:px-6">
          <div class="hidden items-center gap-2 text-[10px] text-slate-500 sm:flex">
            <font-awesome-icon icon="fa-solid fa-circle-info" class="h-3 w-3 text-teal-600" />
            Metinler PDF'de seçilebilir, bağlantılar tıklanabilir kalır.
          </div>
          <p v-if="exportError" class="mb-0 mr-auto text-xs font-bold text-rose-600">{{ exportError }}</p>
          <button type="button" class="ml-auto inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-5 text-sm font-black text-white shadow-lg shadow-amber-600/20 transition hover:from-amber-700 hover:to-amber-600 disabled:cursor-not-allowed disabled:opacity-50" :disabled="!normalizedTours.length" @click="exportPdf">
            <font-awesome-icon icon="fa-solid fa-file-export" class="h-4 w-4" />
            PDF'i Hazırla
            <span class="rounded-md bg-white/15 px-1.5 py-0.5 text-[9px]">{{ settings.format === 'compact' ? 'Compact' : 'Detaylı' }}</span>
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, watch } from 'vue'
import { buildTourPdfDocument, normalizeTourForPdf } from '~/utils/tourPdf'

const props = defineProps({
  open: { type: Boolean, default: false },
  tours: { type: Array, default: () => [] },
  locale: { type: String, default: 'tr' },
  pageTitle: { type: String, default: '' },
  coverUrl: { type: String, default: '/assets/img/pdf/teklif_kapak_9.png' },
})

const emit = defineEmits(['update:open', 'remove', 'clear'])
const { showImage, formatMoney } = useCommon()
const auth = useUser()
const isAdmin = computed(() => auth.value?.U_Admin === 'on')

const automaticTitle = computed(() => {
  if (props.tours.length === 1 && props.tours[0]?.tur_Sure) {
    return props.locale === 'tr'
      ? `${props.tours[0].tur_Sure} Gün Umre Tur Teklifi`
      : `${props.tours[0].tur_Sure}-Day Umrah Tour Offer`
  }
  return props.locale === 'tr' ? 'Umre Tur Programları' : 'Umrah Tour Programmes Selected for You'
})
const standardMessage = computed(() => props.locale === 'tr'
  ? 'Seçtiğiniz tur programları bilgilendirme amacıyla hazırlanmıştır. Fiyat, kontenjan ve hizmet kapsamı rezervasyon sırasında yeniden teyit edilir. Kesin bilgiler satış danışmanlarımız tarafından sağlanacaktır.'
  : 'The selected programmes are provided for information. Price, availability and services are reconfirmed at booking. Final details will be provided by our travel consultants.')
const settings = reactive({
  format: 'detailed',
  groupBy: 'month',
  includeCover: true,
  message: '',
  priceNote: props.locale === 'tr'
    ? 'Fiyatlar kişi başıdır ve müsaitliğe göre değişebilir.'
    : 'Prices are per person and subject to availability.',
  highlightFairmont: true,
  highlightTrain: true,
  showCampaigns: true,
})
const effectiveMessage = computed(() => isAdmin.value && settings.message.trim() ? settings.message.trim() : standardMessage.value)
const exportError = ref('')

const origin = computed(() => import.meta.client ? window.location.origin : '')
const absoluteCoverUrl = computed(() => {
  if (/^https?:/i.test(props.coverUrl)) return props.coverUrl
  return `${origin.value}${props.coverUrl.startsWith('/') ? '' : '/'}${props.coverUrl}`
})
const normalizedTours = computed(() => props.tours.map((tour) => normalizeTourForPdf(tour, {
  locale: props.locale,
  origin: origin.value,
  imageResolver: showImage,
  moneyFormatter: formatMoney,
})))

const stats = computed(() => ({
  fairmont: normalizedTours.value.filter((tour) => tour.fairmont).length,
  train: normalizedTours.value.filter((tour) => tour.fastTrain).length,
}))

const previewSummary = computed(() => {
  const prices = normalizedTours.value.filter((tour) => tour.price > 0).sort((a, b) => a.price - b.price)
  return [
    { label: 'Seçilen program', value: String(normalizedTours.value.length) },
    { label: 'Fairmont', value: `${stats.value.fairmont} seçenek` },
    { label: 'Başlangıç fiyatı', value: prices[0] ? `${prices[0].priceLabel} ${prices[0].currency}` : '—' },
  ]
})

const formatOptions = [
  { value: 'compact', label: 'Compact', description: 'Tarih, otel, fiyat ve havayolu tek bakışta.' },
  { value: 'detailed', label: 'Detaylı', description: 'Görselli, premium ve listeleme sayfasına benzer.' },
]

const groupOptions = [
  { value: 'none', label: 'Gruplama yok', icon: 'fa-solid fa-list' },
  { value: 'month', label: 'Aya göre', icon: 'fa-solid fa-calendar' },
  { value: 'duration', label: 'Süreye göre', icon: 'fa-solid fa-clock' },
  { value: 'hotel', label: 'Otele göre', icon: 'fa-solid fa-hotel' },
  { value: 'airline', label: 'Havayoluna göre', icon: 'fa-solid fa-plane' },
]

const closeStudio = () => emit('update:open', false)

const handleEscape = (event) => {
  if (event.key === 'Escape') closeStudio()
}

watch(() => props.open, (isOpen) => {
  if (!import.meta.client) return
  exportError.value = ''
  document.body.style.overflow = isOpen ? 'hidden' : ''
  if (isOpen) window.addEventListener('keydown', handleEscape)
  else window.removeEventListener('keydown', handleEscape)
}, { immediate: true })

watch(() => props.tours.length, (length) => {
  if (props.open && length === 0) closeStudio()
})

onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.body.style.overflow = ''
  window.removeEventListener('keydown', handleEscape)
})

const exportPdf = () => {
  exportError.value = ''
  if (!normalizedTours.value.length || !import.meta.client) return
  const popup = window.open('', '_blank', 'width=1180,height=900')
  if (!popup) {
    exportError.value = 'Tarayıcı yeni pencereyi engelledi. Açılır pencerelere izin verip tekrar deneyin.'
    return
  }

  const html = buildTourPdfDocument({
    tours: normalizedTours.value,
    settings: {
      ...settings,
      title: automaticTitle.value,
      message: effectiveMessage.value,
      includeBrandPage: settings.includeCover,
    },
    locale: props.locale,
    origin: origin.value,
    logoUrl: '/assets/img/logo/inanc_tur_yeni_logo.webp',
    coverUrl: props.coverUrl,
  })
  popup.document.open()
  popup.document.write(html)
  popup.document.close()
  popup.addEventListener('load', () => {
    window.setTimeout(() => {
      popup.focus()
      popup.print()
    }, 700)
  }, { once: true })
}
</script>
